import { updateUserRole } from "@/models/socket/admin.model";
import { Button } from "@/shadcn/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shadcn/components/ui/dialog";
import { genericFailureToast, genericSuccessToast } from "@/utils/utils.util";
import React, { useState } from "react";

type UserRejectionDialogProps = {
  userFullName: string;
  userID: string;
  updateRole: (
    userId: string,
    roleAction: "approve" | "block" | "admin"
  ) => void;
  onClose: () => void;
};

const UserRejectionDialog: React.FC<UserRejectionDialogProps> = ({
  userID,
  userFullName,
  updateRole,
  onClose,
}) => {
  const [rejectReason, setRejectReason] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  async function rejectUser() {
    if (!rejectReason.trim()) {
      setError("A rejection reason is required.");
      return;
    }

    if (rejectReason.length > 255) {
      setError("Rejection reason cannot exceed 255 characters.");
      return;
    }

    setSubmitting(true);
    setError(null);

    const response = await updateUserRole(userID, "block", rejectReason.trim());
    if (response.rStatus === "error") {
      console.error("Failed to update user role:", response.rData);
      genericFailureToast(`Failed to block user`);
      return;
    }

    genericSuccessToast("User blocked successfully");
    updateRole(userID, "block"); // update role in parent component
    setSubmitting(false);
    onClose(); // close dialog after successful update
  }

  return (
    <Dialog
      open={true}
      onOpenChange={(open) => {
        if (!open) {
          setTimeout(() => {
            onClose(); // call onClose when dialog is closed
          }, 100); // reset state after dialog closes
        }
      }}
    >
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Block {userFullName}</DialogTitle>
          <DialogDescription></DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <p>
            Are you sure you want to block {userFullName}? <br />
            <br />
            Please provide a reason for blocking this user.
          </p>
          {error && <div className="text-sm text-red-500">{error}</div>}
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Enter reason for blocking the user"
            className="w-full rounded border p-2"
            rows={4}
          />
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button className="" onClick={rejectUser} disabled={submitting}>
            {submitting ? "Submitting..." : "Block User"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserRejectionDialog;
